/**
 * Work Package Manager Component
 * Create and manage work packages for coordinated execution
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Stack,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Assignment as WorkIcon,
  Group as TeamIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  CheckCircle as CompleteIcon,
  PlayArrow as StartIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { format } from 'date-fns';

import { useSiteContext } from '../../hooks/useSiteContext';
import plannedWorksService from '../../services/plannedWorksService';
import {
  WorkPackage,
  PlannedWorkListItem,
  CreateWorkPackageRequest,
  User
} from '../../types/plannedWorks';

interface WorkPackageManagerProps {
  open: boolean;
  onClose: () => void;
  onPackageCreated?: (pkg: WorkPackage) => void;
  existingPackage?: WorkPackage;
  availableWorks?: PlannedWorkListItem[];
}

interface PackageFormData {
  name: string;
  description: string;
  coordinator_id: number | null;
  planned_start_date: Date | null;
  planned_completion_date: Date | null;
  max_concurrent_works: number;
  requires_coordination: boolean;
  special_instructions: string;
}

export const WorkPackageManager: React.FC<WorkPackageManagerProps> = ({
  open,
  onClose,
  onPackageCreated,
  existingPackage,
  availableWorks = []
}) => {
  const { currentSite } = useSiteContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form data
  const [formData, setFormData] = useState<PackageFormData>({
    name: '',
    description: '',
    coordinator_id: null,
    planned_start_date: null,
    planned_completion_date: null,
    max_concurrent_works: 2,
    requires_coordination: true,
    special_instructions: ''
  });
  
  // Package works
  const [packageWorks, setPackageWorks] = useState<PlannedWorkListItem[]>([]);
  const [availableWorksList, setAvailableWorksList] = useState<PlannedWorkListItem[]>([]);
  
  // Reference data
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    if (open) {
      fetchUsers();
      if (existingPackage) {
        loadExistingPackage();
      } else {
        setAvailableWorksList(availableWorks);
      }
    }
  }, [open, existingPackage, availableWorks]);

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/v1/users/site-users/');
      const data = await response.json();
      setUsers(data.results || data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const loadExistingPackage = async () => {
    if (!existingPackage) return;
    
    try {
      setFormData({
        name: existingPackage.name,
        description: existingPackage.description,
        coordinator_id: existingPackage.coordinator?.id || null,
        planned_start_date: existingPackage.planned_start_date ? new Date(existingPackage.planned_start_date) : null,
        planned_completion_date: existingPackage.planned_completion_date ? new Date(existingPackage.planned_completion_date) : null,
        max_concurrent_works: existingPackage.max_concurrent_works,
        requires_coordination: existingPackage.requires_coordination,
        special_instructions: existingPackage.special_instructions || ''
      });
      
      // Load package works
      const packageDetails = await plannedWorksService.workPackages.get(existingPackage.id);
      setPackageWorks(packageDetails.works || []);
      
      // Filter available works (exclude those already in package)
      const packageWorkIds = (packageDetails.works || []).map(w => w.id);
      setAvailableWorksList(availableWorks.filter(w => !packageWorkIds.includes(w.id)));
    } catch (err) {
      console.error('Failed to load package details:', err);
      setError('Failed to load package details');
    }
  };

  const addWorkToPackage = (work: PlannedWorkListItem) => {
    setPackageWorks(prev => [...prev, work]);
    setAvailableWorksList(prev => prev.filter(w => w.id !== work.id));
  };

  const removeWorkFromPackage = (work: PlannedWorkListItem) => {
    setPackageWorks(prev => prev.filter(w => w.id !== work.id));
    setAvailableWorksList(prev => [...prev, work]);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination } = result;
    
    if (source.droppableId === destination.droppableId) {
      // Reordering within same list
      if (source.droppableId === 'package-works') {
        const items = Array.from(packageWorks);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);
        setPackageWorks(items);
      }
    } else {
      // Moving between lists
      if (source.droppableId === 'available-works' && destination.droppableId === 'package-works') {
        const work = availableWorksList[source.index];
        addWorkToPackage(work);
      } else if (source.droppableId === 'package-works' && destination.droppableId === 'available-works') {
        const work = packageWorks[source.index];
        removeWorkFromPackage(work);
      }
    }
  };

  const calculateTotalDuration = () => {
    return packageWorks.reduce((total, work) => total + (work.estimated_duration_hours || 0), 0);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!currentSite?.siteId) {
        throw new Error('No site selected');
      }

      const request: CreateWorkPackageRequest = {
        ...formData,
        site: currentSite.siteId,
        works: packageWorks.map(w => w.id)
      };

      let result: WorkPackage;
      if (existingPackage) {
        result = await plannedWorksService.workPackages.update(existingPackage.id, request);
      } else {
        result = await plannedWorksService.workPackages.create(request);
      }
      
      onPackageCreated?.(result);
      onClose();
    } catch (err) {
      console.error('Failed to save package:', err);
      setError(err instanceof Error ? err.message : 'Failed to save package');
    } finally {
      setLoading(false);
    }
  };

  const WorkItem: React.FC<{ work: PlannedWorkListItem; index: number; provided: any }> = ({ work, index, provided }) => (
    <Card
      ref={provided.innerRef}
      {...provided.draggableProps}
      sx={{ mb: 1, opacity: provided.isDragging ? 0.8 : 1 }}
    >
      <CardContent sx={{ py: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box {...provided.dragHandleProps}>
            <DragIcon color="action" />
          </Box>
          
          <WorkIcon color="primary" />
          
          <Box flex={1}>
            <Typography variant="body2" fontWeight="medium">
              {work.title}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {work.estimated_duration_hours}h • Priority: {work.priority}
            </Typography>
          </Box>
          
          <Chip
            size="small"
            label={work.status_display}
            color={work.status === 'completed' ? 'success' : work.status === 'in_progress' ? 'primary' : 'default'}
          />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle>
        {existingPackage ? 'Edit Work Package' : 'Create Work Package'}
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Package Details */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 'fit-content' }}>
              <Typography variant="h6" gutterBottom>
                Package Details
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Package Name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                    value={users.find(user => user.id === formData.coordinator_id) || null}
                    onChange={(_, value) => setFormData(prev => ({ ...prev, coordinator_id: value?.id || null }))}
                    renderInput={(params) => (
                      <TextField {...params} label="Package Coordinator" />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                          {option.first_name[0]}{option.last_name[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {option.first_name} {option.last_name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {option.role}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Planned Start"
                    value={formData.planned_start_date}
                    onChange={(date) => setFormData(prev => ({ ...prev, planned_start_date: date }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <DateTimePicker
                    label="Planned Completion"
                    value={formData.planned_completion_date}
                    onChange={(date) => setFormData(prev => ({ ...prev, planned_completion_date: date }))}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Concurrent Works"
                    value={formData.max_concurrent_works}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_concurrent_works: Number(e.target.value) }))}
                    inputProps={{ min: 1, max: 10 }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Special Instructions"
                    value={formData.special_instructions}
                    onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
                    placeholder="Coordination requirements, dependencies, etc."
                  />
                </Grid>
              </Grid>
              
              {/* Package Summary */}
              {packageWorks.length > 0 && (
                <Box mt={3}>
                  <Typography variant="subtitle2" gutterBottom>
                    Package Summary
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <Chip
                      size="small"
                      icon={<WorkIcon />}
                      label={`${packageWorks.length} works`}
                    />
                    <Chip
                      size="small"
                      icon={<ScheduleIcon />}
                      label={`${calculateTotalDuration()}h total`}
                    />
                  </Stack>
                </Box>
              )}
            </Paper>
          </Grid>
          
          {/* Work Assignment */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Work Assignment
              </Typography>
              
              <DragDropContext onDragEnd={handleDragEnd}>
                <Grid container spacing={2}>
                  {/* Available Works */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Available Works
                    </Typography>
                    
                    <Droppable droppableId="available-works">
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          sx={{
                            minHeight: 200,
                            maxHeight: 400,
                            overflow: 'auto',
                            p: 1,
                            border: '2px dashed',
                            borderColor: snapshot.isDraggingOver ? 'primary.main' : 'grey.300',
                            borderRadius: 1,
                            bgcolor: snapshot.isDraggingOver ? 'action.hover' : 'transparent'
                          }}
                        >
                          {availableWorksList.map((work, index) => (
                            <Draggable key={work.id} draggableId={`available-${work.id}`} index={index}>
                              {(provided) => (
                                <WorkItem work={work} index={index} provided={provided} />
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          
                          {availableWorksList.length === 0 && (
                            <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
                              No available works
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Droppable>
                  </Grid>
                  
                  {/* Package Works */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Package Works
                    </Typography>
                    
                    <Droppable droppableId="package-works">
                      {(provided, snapshot) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          sx={{
                            minHeight: 200,
                            maxHeight: 400,
                            overflow: 'auto',
                            p: 1,
                            border: '2px dashed',
                            borderColor: snapshot.isDraggingOver ? 'success.main' : 'grey.300',
                            borderRadius: 1,
                            bgcolor: snapshot.isDraggingOver ? 'success.light' : 'transparent'
                          }}
                        >
                          {packageWorks.map((work, index) => (
                            <Draggable key={work.id} draggableId={`package-${work.id}`} index={index}>
                              {(provided) => (
                                <WorkItem work={work} index={index} provided={provided} />
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                          
                          {packageWorks.length === 0 && (
                            <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
                              Drag works here to add to package
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Droppable>
                  </Grid>
                </Grid>
              </DragDropContext>
              
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Tip:</strong> Drag and drop works between lists to organize your package. 
                  Works in a package can be coordinated and executed together.
                </Typography>
              </Alert>
            </Paper>
          </Grid>
        </Grid>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.name || packageWorks.length === 0}
        >
          {loading ? 'Saving...' : existingPackage ? 'Update Package' : 'Create Package'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkPackageManager;
